import React, { useCallback } from 'react';
import { withStyles } from '@material-ui/styles';
import { Form, Formik } from 'formik';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import { RadioGroup } from '@material-ui/core';
import clsx from 'clsx';
import { translate } from '../../../i18n/I18nProvider';

import styles, { customStyles } from '../styles/phoneNumbers';
import SelectRow from '../../../partials/dataRows/SelectRow';
import validateSchema from '../actions/validateSchema';
import Optional from '../../../utils/optional';
import { attorneySubmit } from '../actions/attorneyRequest';
import { actions as reduxActions } from '../reducers';
import CustomFormControlLabel from '../../../partials/customFormControlLabel';
import PrimaryButton from '../../../partials/customButtons/PrimaryButton';
import SecondaryButton from '../../../partials/customButtons/SecondaryButton';
import PortNumberCheck from './PortNumberCheck';
import {
  isDefined,
  isStringDefined,
  isStringEmpty,
  isUndefined
} from '../../../utils/isDefined';
import AvailableNumbers from './AvailableNumbers';
import NewNumbers from './NewNumbers';

const PhoneNumbers = ({
  classes,
  selectOptions,
  secondStepData,
  setUserData,
  handleNext,
  resestUserWizard,
  handleBack,
  setCheckNumberData,
  onCloseAttempt
}) => {
  const onBackClick = useCallback(
    () => {
      setUserData(null, 'secondStep');
      return handleBack();
    },
    // eslint-disable-next-line
    []
  );

  const onSubmit = useCallback(
    async values => {
      let stepSize = 2;

      if (
        isUndefined(values.mobile_number) ||
        isStringEmpty(values.mobile_number)
      ) {
        stepSize = 3;
      } else if (
        values.selected === 'port' &&
        isDefined(values.mobile_number)
      ) {
        stepSize = 1;
      }

      const isNewNumber =
        (values.selected === 'new' && isStringDefined(values.mobile_number)) ||
        (values.selected === 'available' &&
          isStringDefined(values.mobile_number));

      handleNext(stepSize);
      setUserData({ ...values, isNewNumber }, 'secondStep');
    },
    // eslint-disable-next-line
    []
  );

  return (
    <div className={classes.root}>
      <Formik
        initialValues={{ selected: 'none', ...secondStepData }}
        enableReinitialize
        validateOnChange={false}
        onSubmit={onSubmit}
        validationSchema={validateSchema({}).numbersStep}
      >
        {({
          handleSubmit,
          errors,
          values,
          handleBlur,
          handleChange,
          setFieldValue
        }) => {
          const fieldProps = {
            values,
            handleBlur,
            handleChange,
            errors
          };

          const onRadioChange = event => {
            const { value } = event.target;
            setFieldValue('selected', value);
            setFieldValue('mobile_number', null);
            setUserData({ selected: value, mobile_number: null }, 'secondStep');
            if (value !== 'port') {
              setCheckNumberData({});
              setUserData({ current_operator: '' }, 'thirdStep');
            }
          };

          const setFieldValueAndSync = (field, value) => {
            setFieldValue(field, value);
            setUserData({ [field]: value }, 'secondStep');
          };

          const clearMobileNumber = () =>
            setFieldValueAndSync('mobile_number', null);
          return (
            <Form
              onSubmit={handleSubmit}
              className={classes.phoneNumbersContainer}
            >
              <SelectRow
                name="landline_number"
                label="REQUEST_NEW_USER.FIXED"
                isEditModal={false}
                options={[
                  ...filterSelectOptions(
                    selectOptions.publicNumbersSelectOptions,
                    values.internal_number
                  )
                ]}
                setFieldValue={setFieldValueAndSync}
                customStyles={customStyles.select}
                maxMenuHeight={200}
                {...fieldProps}
              />
              <SelectRow
                name="internal_number"
                label="REQUEST_NEW_USER.LOCAL"
                isEditModal={false}
                options={[
                  ...filterSelectOptions(
                    selectOptions.privateNumbersSelectOptions,
                    values.landline_number
                  )
                ]}
                setFieldValue={setFieldValueAndSync}
                customStyles={customStyles.select}
                maxMenuHeight={200}
                {...fieldProps}
              />
              {errors.atLeastOneNumber && (
                <div className={classes.atLeastOneError}>
                  {errors.atLeastOneNumber}
                </div>
              )}
              <div className={classes.radioButtonsContainer}>
                <div>Mobile number</div>
                <RadioGroup
                  className={classes.radioButtons}
                  value={values.selected}
                  onChange={onRadioChange}
                >
                  <CustomFormControlLabel
                    value="none"
                    label={translate('REQUEST_NEW_USER.NO_MOBILE_ADDED_LATER')}
                  />
                  <CustomFormControlLabel
                    value="available"
                    label={
                      <div className={classes.mobileNumberContainer}>
                        <div>
                          {translate(
                            'REQUEST_NEW_USER.ACTIVATE_AVAILABLE_NUMBER'
                          )}
                        </div>
                        <div
                          className={clsx(classes.mobileNumber, {
                            [classes.hidden]: values.selected !== 'available'
                          })}
                        >
                          {values.mobile_number}
                          {isDefined(values.mobile_number) && (
                            <i
                              className={clsx(
                                'fas fa-times',
                                classes.clearIcon
                              )}
                              onClick={clearMobileNumber}
                            />
                          )}
                        </div>
                      </div>
                    }
                  />
                  <CustomFormControlLabel
                    value="port"
                    label={
                      <div className={classes.mobileNumberContainer}>
                        <div>
                          {translate('REQUEST_NEW_USER.PORT_MOBILE_NUMBER')}
                        </div>
                        <div
                          className={clsx(classes.mobileNumber, {
                            [classes.hidden]: values.selected !== 'port'
                          })}
                        >
                          {values.mobile_number}
                          {isDefined(values.mobile_number) && (
                            <i
                              className={clsx(
                                'fas fa-times',
                                classes.clearIcon
                              )}
                              onClick={clearMobileNumber}
                            />
                          )}
                        </div>
                      </div>
                    }
                  />
                  <CustomFormControlLabel
                    value="new"
                    label={
                      <div className={classes.mobileNumberContainer}>
                        <div>
                          {translate('REQUEST_NEW_USER.ACTIVATE_NEW_NUMBER')}
                        </div>
                        <div
                          className={clsx(classes.mobileNumber, {
                            [classes.hidden]: values.selected !== 'new'
                          })}
                        >
                          {values.mobile_number}
                          {isDefined(values.mobile_number) && (
                            <i
                              className={clsx(
                                'fas fa-times',
                                classes.clearIcon
                              )}
                              onClick={clearMobileNumber}
                            />
                          )}
                        </div>
                      </div>
                    }
                  />
                </RadioGroup>
              </div>
              <div>
                {values.selected === 'available' && (
                  <AvailableNumbers
                    setFieldValue={setFieldValueAndSync}
                    values={values}
                  />
                )}
                {values.selected === 'port' && (
                  <PortNumberCheck
                    setFieldValue={setFieldValueAndSync}
                    values={values}
                  />
                )}
                {values.selected === 'new' && (
                  <NewNumbers
                    setFieldValue={setFieldValueAndSync}
                    newNumber={true}
                    values={values}
                  />
                )}
              </div>
              <div className={classes.footer}>
                <PrimaryButton onClick={onBackClick}>
                  {translate('BUTTON.BACK')}
                </PrimaryButton>
                <PrimaryButton onClick={handleSubmit}>
                  {translate('BUTTON.NEXT')}
                </PrimaryButton>
                <SecondaryButton onClick={onCloseAttempt || resestUserWizard}>
                  {translate('BUTTON.CANCEL')}
                </SecondaryButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const mapStatesToProps = ({ requestUserWizard }) => {
  return {
    secondStepData: requestUserWizard.userData.secondStep,
    selectOptions: requestUserWizard.selectOptions
  };
};

const mapDispatchToProps = {
  ...reduxActions,
  attorneySubmit
};

export default flowRight(connect(mapStatesToProps, mapDispatchToProps))(
  withStyles(styles)(PhoneNumbers)
);

const filterSelectOptions = (selectOptions, optionsToFilter) => {
  const filteredOptions = Optional(selectOptions)
    .map(options => options.filter(option => option.value !== optionsToFilter))
    .or([]);

  return [{ value: '', label: '' }, ...filteredOptions];
};
